import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { templates, channelWa } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, Card } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader, Stepper } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { useUnsavedGuard } from '../../../lib/useUnsavedGuard';
import { validateTemplate, validateName, advisoriesFor } from '../../../lib/template-validation';
import { MetaRefusalNotice, refusalFrom, type Refusal } from '../../numbers/MetaRefusalNotice';
import { LibraryBrowser } from './LibraryBrowser';
import { BasicsStep } from './BasicsStep';
import { ContentStep } from './ContentStep';
import { ReviewStep } from './ReviewStep';
import {
  componentsFromDraft, draftFromComponents, emptyDraft, normaliseName, sameDraft,
  type TemplateDraft
} from './draft';
import '../templates.css';

/*
 * Authoring a template, as a page.
 *
 * This replaces a drawer that slid over the list and asked for Meta's
 * `components` JSON in a textarea. Everything that made that drawer hard is
 * structural: there is not room beside a list for a form and a preview; a
 * drawer cannot be linked to, bookmarked or reloaded; and four unrelated
 * decisions (where to start, what to call it, what it says, whether to submit)
 * were one long scroll with no order to them.
 *
 * Four steps, and the first of them is the one that was missing entirely: a
 * customer whose WhatsApp account is new has nothing to start from, and the
 * page they met was an empty list. Now it offers Meta's own library, whatever
 * is already on their WhatsApp account, or a blank one.
 */

/** The four steps when creating. Editing drops the first: it has already been made. */
const CREATE_STEPS = ['start', 'basics', 'content', 'review'] as const;
const EDIT_STEPS = ['basics', 'content', 'review'] as const;

type Start = 'library' | 'import' | 'blank';

export default function TemplateWizardPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [params] = useSearchParams();
  const editing = Boolean(id);

  const steps = editing ? EDIT_STEPS : CREATE_STEPS;
  /** Index into `steps`. The problem list jumps by the CREATE index, so it is offset when editing. */
  const [step, setStep] = useState(0);

  const [draft, setDraft] = useState<TemplateDraft>(emptyDraft);
  const [saved, setSaved] = useState<TemplateDraft>(emptyDraft);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [raw, setRaw] = useState('[]');
  const [lossless, setLossless] = useState(true);
  const [start, setStart] = useState<Start | null>(
    params.get('from') === 'library' ? 'library' : params.get('from') === 'blank' ? 'blank' : null
  );
  const [entry, setEntry] = useState<channelWa.TemplateLibraryEntry | null>(null);
  const [busy, setBusy] = useState<'draft' | 'submit' | 'import' | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const [nameTaken, setNameTaken] = useState(false);

  // `/templates/new?from=blank` is the "write your own" card on the list page:
  // it should land on the first question, not on the chooser it just answered.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current || editing || params.get('clone')) return;
    if (params.get('from') === 'blank') { deepLinked.current = true; setStep(1); }
  }, [editing, params]);

  // ── Loading an existing template ─────────────────────────────────────────
  // `?clone=<id>` is the duplicate action from the list, which is nearly
  // always "the same template in a second language". It loads the source and
  // opens as a NEW template, so the original is never touched.
  const cloneId = params.get('clone');
  const sourceId = id ?? cloneId;

  const existing = useQuery({
    queryKey: ['template', sourceId],
    queryFn: () => templates.get(sourceId!),
    enabled: Boolean(sourceId)
  });

  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    const tpl = existing.data?.data;
    if (!tpl || loadedFor.current === tpl.id) return;
    loadedFor.current = tpl.id;
    const parsed = draftFromComponents(tpl.components, {
      name: editing ? tpl.code : `${tpl.code}_copy`,
      language: tpl.language,
      category: tpl.category ?? 'utility'
    });
    setDraft(parsed.draft);
    // A clone has never been saved, so everything about it is unsaved work.
    setSaved(editing ? parsed.draft : emptyDraft());
    setLossless(parsed.lossless);
    setRaw(tpl.components ?? '[]');
    // A template the form cannot fully show opens in the raw editor rather
    // than in a form that would quietly drop what it cannot draw.
    if (!parsed.lossless) setMode('json');
    setNameTouched(true);
    if (!editing) { setStart('blank'); setStep(1); }
  }, [existing.data, editing]);

  // ── What gets sent ───────────────────────────────────────────────────────
  const components = mode === 'json' ? raw : componentsFromDraft(draft);
  const problems = useMemo(
    () => [...validateName(normaliseName(draft.name)), ...validateTemplate(components, draft.category)],
    [components, draft.category, draft.name]
  );
  const advisories = useMemo(() => advisoriesFor(components), [components]);
  const blocked = problems.length > 0;

  const dirty = !sameDraft(draft, saved) && !(step === 0 && !editing && start === null);
  useUnsavedGuard(dirty && busy === null, {
    title: t('templates.wizard.leave.title'),
    body: t('templates.wizard.leave.body'),
    discard: t('templates.wizard.leave.discard'),
    keep: t('templates.wizard.leave.keep')
  });

  // Whether the wording is still Meta's own, which is what makes the adopt
  // path (and its faster review) honest. One edit and it is the customer's
  // template, submitted the ordinary way.
  const unchangedFromLibrary = useMemo(() => {
    if (!entry) return false;
    try {
      return JSON.stringify(JSON.parse(entry.components)) === JSON.stringify(JSON.parse(components));
    } catch {
      return false;
    }
  }, [entry, components]);

  function chooseLibrary(chosen: channelWa.TemplateLibraryEntry) {
    const parsed = draftFromComponents(chosen.components, {
      name: chosen.name,
      language: chosen.language,
      category: chosen.category
    });
    setEntry(chosen);
    setDraft(parsed.draft);
    setLossless(parsed.lossless);
    setRaw(chosen.components);
    setMode(parsed.lossless ? 'form' : 'json');
    setNameTouched(true);
    setStep(1);
  }

  function chooseBlank() {
    setEntry(null);
    setDraft(emptyDraft());
    setLossless(true);
    setMode('form');
    setRaw('[]');
    setNameTouched(false);
    setStep(1);
  }

  /*
   * "Import what is already on your WhatsApp account" was a real endpoint with
   * no button anybody would find: it lived in a card under the table on the
   * list page. An org that already has a catalogue should never retype it, so
   * it is one of the three ways in.
   */
  async function importFromMeta() {
    setBusy('import');
    try {
      const res = await templates.importFromMeta();
      qc.invalidateQueries({ queryKey: ['templates'] });
      notifySuccess(
        t('templates.toast.imported', {
          imported: res.data.imported,
          updated: res.data.updated,
          skipped: res.data.skipped
        }),
        t('templates.title')
      );
      nav('/templates');
    } catch (e) {
      notifyError(importError(e, t), t('templates.toast.importFailed'));
    } finally {
      setBusy(null);
    }
  }

  async function save(submit: boolean) {
    if (submit && blocked) return;
    setBusy(submit ? 'submit' : 'draft');
    setRefusal(null);
    const code = normaliseName(draft.name);

    try {
      let templateId = id ?? null;

      if (editing && templateId) {
        await templates.update(templateId, { category: draft.category, components });
      } else if (entry && entry.source === 'meta' && unchangedFromLibrary) {
        /*
         * Meta's own wording, unedited, so it is created at Meta by naming the
         * library entry rather than by posting components. That is the whole
         * commercial point of the library: Meta has already reviewed these
         * words, so approval is normally much faster.
         */
        const res = await channelWa.adoptLibraryTemplate({
          key: entry.key,
          name: code,
          language: draft.language,
          category: draft.category,
          submit
        });
        templateId = res.data.id;
        setSaved(draft);
        finish(res.data.id, submit);
        return;
      } else {
        const res = await templates.create({
          code,
          category: draft.category,
          language: draft.language,
          components
        });
        templateId = res.data.id;
      }

      if (submit && templateId) await templates.submit(templateId);
      setSaved(draft);
      finish(templateId!, submit);
    } catch (e) {
      const r = refusalFrom(e);
      if (r) { setRefusal(r); return; }
      /*
       * The one refusal that is nobody's fault and has an obvious fix: this
       * account already has a template with that name in that language. Say so
       * on the field, on the step that owns it, rather than in a toast that
       * leaves the person staring at a review screen with nothing marked.
       */
      if ((e as { code?: string } | null)?.code === 'DUPLICATE') {
        setNameTaken(true);
        setStep(editing ? 0 : 1);
        return;
      }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'),
        submit ? t('templates.toast.submitFailed') : t('toast.saveFailed'));
    } finally {
      setBusy(null);
    }
  }

  function finish(templateId: string, submitted: boolean) {
    qc.invalidateQueries({ queryKey: ['templates'] });
    notifySuccess(
      submitted ? t('templates.toast.submittedNamed', { code: normaliseName(draft.name) })
        : t('templates.toast.saved', { code: normaliseName(draft.name) }),
      t('templates.title')
    );
    nav(`/templates/${templateId}`);
  }

  function leave() {
    if (!dirty) { nav('/templates'); return; }
    openConfirmModal({
      title: t('templates.wizard.leave.title'),
      children: <Text role="bodySmall">{t('templates.wizard.leave.body')}</Text>,
      labels: { confirm: t('templates.wizard.leave.discard'), cancel: t('templates.wizard.leave.keep') },
      confirmProps: { color: 'red' },
      onConfirm: () => { setSaved(draft); nav('/templates'); }
    });
  }

  /** The problem list numbers steps as if creating; editing has one fewer. */
  const stepOffset = editing ? 1 : 0;
  const current = steps[step];

  const nameProblem = nameTouched && normaliseName(draft.name).length === 0
    ? t('templates.wizard.name.required')
    : nameTaken
      ? t('templates.wizard.name.taken')
      : undefined;

  const canAdvance =
    current === 'basics' ? normaliseName(draft.name).length > 0
      : current === 'content' ? true
        : true;

  return (
    <Stack gap="lg">
      <PageHeader
        title={editing ? t('templates.wizard.title.edit') : t('templates.wizard.title.new')}
        description={editing ? t('templates.wizard.description.edit') : t('templates.wizard.description.new')}
        withDivider={false}
        actions={<Group gap="xs"><HelpButton guide="automation" /><Button variant="default" size="sm" onClick={leave}>{t('action.cancel')}</Button></Group>}
      />

      {refusal && <MetaRefusalNotice refusal={refusal} onClose={() => setRefusal(null)} />}
      {editing && existing.error && <Alert color="error" role="alert">{t('templates.loadError')}</Alert>}

      <Card withBorder className="rr-tplwiz" data-guide="template-wizard">
        <Stepper
          active={step}
          size="xs"
          orientation="horizontal"
          allowNextStepsSelect={false}
          onStepClick={(i) => { if (i < step) setStep(i); }}
        >
          {steps.map((s) => (
            <Stepper.Step key={s} label={t(`templates.wizard.step.${s}`)} description={t(`templates.wizard.step.${s}.hint`)} />
          ))}
        </Stepper>

        <div className="rr-tplwiz__body">
          {current === 'start' && (
            <Stack gap="lg">
              <div className="rr-tplwiz__ways">
                <WayIn
                  active={start === 'library'}
                  title={t('templates.wizard.way.library')}
                  body={t('templates.wizard.way.library.body')}
                  action={t('templates.wizard.way.library.action')}
                  onClick={() => setStart('library')}
                  icon={<LibraryIcon />}
                />
                <WayIn
                  active={start === 'import'}
                  title={t('templates.wizard.way.import')}
                  body={t('templates.wizard.way.import.body')}
                  action={t('templates.wizard.way.import.action')}
                  loading={busy === 'import'}
                  onClick={() => { setStart('import'); void importFromMeta(); }}
                  icon={<ImportIcon />}
                />
                <WayIn
                  active={start === 'blank'}
                  title={t('templates.wizard.way.blank')}
                  body={t('templates.wizard.way.blank.body')}
                  action={t('templates.wizard.way.blank.action')}
                  onClick={() => { setStart('blank'); chooseBlank(); }}
                  icon={<PencilIcon />}
                />
              </div>

              {start === 'library' && (
                <LibraryBrowser onChoose={chooseLibrary} chosenKey={entry?.key ?? null} />
              )}
            </Stack>
          )}

          {current === 'basics' && (
            <BasicsStep
              draft={draft}
              onChange={(next) => { setDraft(next); setNameTouched(true); setNameTaken(false); }}
              nameError={nameProblem}
              lockIdentity={editing}
            />
          )}

          {current === 'content' && (
            <ContentStep
              draft={draft}
              onChange={setDraft}
              raw={raw}
              onRawChange={setRaw}
              mode={mode}
              onModeChange={(next) => {
                if (next === 'json') { setRaw(componentsFromDraft(draft)); setMode('json'); return; }
                const parsed = draftFromComponents(raw, {
                  name: draft.name, language: draft.language, category: draft.category
                });
                setDraft(parsed.draft);
                setLossless(parsed.lossless);
                setMode(parsed.lossless ? 'form' : 'json');
              }}
              lossless={lossless}
            />
          )}

          {current === 'review' && (
            <ReviewStep
              draft={draft}
              components={components}
              problems={problems}
              advisories={advisories}
              librarySource={entry && unchangedFromLibrary ? entry.source : null}
              onGoToStep={(i) => setStep(Math.max(0, i - stepOffset))}
            />
          )}
        </div>

        <div className="rr-tplwiz__foot">
          <Button
            variant="default"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            {t('templates.wizard.back')}
          </Button>

          <Group gap="xs">
            {current === 'review' ? (
              <>
                <Button
                  variant="default"
                  loading={busy === 'draft'}
                  disabled={busy !== null}
                  onClick={() => void save(false)}
                >
                  {t('templates.actions.saveDraft')}
                </Button>
                <Button
                  loading={busy === 'submit'}
                  disabled={blocked || busy !== null}
                  onClick={() => void save(true)}
                >
                  {t('templates.actions.submit')}
                </Button>
              </>
            ) : (
              <Button
                disabled={!canAdvance || (current === 'start' && start !== 'library' && start !== 'blank')}
                onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              >
                {t('templates.wizard.next')}
              </Button>
            )}
          </Group>
        </div>
      </Card>
    </Stack>
  );
}

function WayIn({
  title, body, action, icon, active, loading, onClick
}: {
  title: string;
  body: string;
  action: string;
  icon: React.ReactNode;
  active: boolean;
  loading?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`rr-tplwiz__way${active ? ' is-active' : ''}`}
      onClick={onClick}
      disabled={loading}
    >
      <span className="rr-tplwiz__way-icon" aria-hidden>{icon}</span>
      <span className="rr-tplwiz__way-title">{title}</span>
      <span className="rr-tplwiz__way-body">{body}</span>
      <span className="rr-tplwiz__way-action">{loading ? '…' : action}</span>
    </button>
  );
}

/**
 * The import has three documented failure modes and each one has a different
 * fix, so "Import failed" on its own sends the user nowhere.
 */
function importError(e: unknown, t: (k: string, v?: Record<string, string | number>) => string): string {
  const code = (e as { code?: string } | null)?.code;
  if (code === 'NO_WABA_ID') return t('templates.import.noWaba');
  if (code === 'not_configured') return t('templates.import.notConfigured');
  if (code === 'META_ERROR') return t('templates.import.metaError');
  return e instanceof Error ? e.message : t('common.unknownError');
}

function LibraryIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 4.5h5a2.5 2.5 0 0 1 2.5 2.5v12a2 2 0 0 0-2-2H4z" />
      <path d="M20 4.5h-5A2.5 2.5 0 0 0 12.5 7v12a2 2 0 0 1 2-2H20z" />
    </svg>
  );
}
function ImportIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 15.5A4.5 4.5 0 0 1 6 7a5.5 5.5 0 0 1 10.6-1.3A4 4 0 0 1 20 13.6" />
      <path d="M12 10.5v7" />
      <polyline points="9,14.5 12,17.5 15,14.5" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16.5 3.8 20.2 7.5 8.6 19H5v-3.6z" />
      <path d="M14.6 5.7 18.3 9.4" />
    </svg>
  );
}
